import { useState, useEffect } from "react";
import {
  Loader2, AlertTriangle, CheckCircle2, ArrowRight, Target, ChevronDown, ChevronUp
} from "lucide-react";
import { Progress } from "@/components/ui/progress";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  Blocked: { bg: "bg-red-500/15", border: "border-red-500/40", text: "text-red-400", bar: "bg-red-500" },
  Partial: { bg: "bg-amber-500/15", border: "border-amber-500/40", text: "text-amber-400", bar: "bg-amber-500" },
  Open: { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-400", bar: "bg-emerald-500" },
};

const KOSHA_COLORS = {
  annamaya: "text-red-400",
  pranamaya: "text-orange-400",
  manomaya: "text-amber-400",
  vijnanamaya: "text-sky-400",
  anandamaya: "text-violet-400",
};

export default function KoshaQuizResultsView({ token, clientId, quizType = "life" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedKosha, setExpandedKosha] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/kosha-quiz/results/${quizType}/${clientId}`, {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        setData(json);
      } catch (e) { console.error("Failed to load kosha quiz results", e); }
      setLoading(false);
    })();
  }, [clientId, quizType]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 text-divine-gold animate-spin" />
        <span className="ml-2 text-chakra-crown/50 text-sm">Loading Kosha Quiz...</span>
      </div>
    );
  }

  if (!data?.completed) {
    return (
      <div className="text-center py-6">
        <Target className="w-8 h-8 text-divine-gold/20 mx-auto mb-2" />
        <p className="text-chakra-crown/50 text-sm">
          Client has not completed the {quizType === "relationship" ? "Relationship" : "Life"} Kosha Quiz yet
        </p>
      </div>
    );
  }

  const { scores } = data;
  if (!scores) return null;

  const koshaOrder = ["annamaya", "pranamaya", "manomaya", "vijnanamaya", "anandamaya"];

  return (
    <div className="space-y-3" data-testid={`kosha-quiz-results-view-${quizType}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="font-heading text-sm text-divine-gold">
          {quizType === "relationship" ? "RELATIONSHIP" : "LIFE"} KOSHA QUIZ
        </h4>
        <div className="flex items-center gap-2">
          <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            Complete
          </span>
        </div>
      </div>

      {/* Overall Score */}
      <div className="p-3 rounded-xl border border-divine-gold/15 bg-divine-gold/5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-chakra-crown/50">Overall Score</span>
          <div className="flex items-center gap-2">
            <span className="font-heading text-base text-divine-gold">
              {scores.overall_score?.toFixed(1)} / 5.0
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_STYLES[scores.overall_status]?.bg} ${STATUS_STYLES[scores.overall_status]?.text}`}>
              {scores.overall_status}
            </span>
          </div>
        </div>
        <Progress value={scores.overall_percentage || 0} className="h-2 bg-cosmic-deep [&>div]:bg-divine-gold" />
        <p className="text-xs text-chakra-crown/40 mt-1.5">{scores.overall_message}</p>
      </div>

      {/* Per-Kosha Scores */}
      <div className="space-y-2">
        {koshaOrder.map(kid => {
          const k = scores.koshas?.[kid];
          if (!k) return null;
          const sc = STATUS_STYLES[k.status] || STATUS_STYLES.Partial;
          const kColor = KOSHA_COLORS[kid] || "text-divine-gold";
          const isExpanded = expandedKosha === kid;

          return (
            <div key={kid}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${sc.bg} ${sc.border}`}
              onClick={() => setExpandedKosha(isExpanded ? null : kid)}
              data-testid={`kosha-result-${kid}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <span className={`font-medium text-sm ${kColor}`}>{k.name}</span>
                  <span className="text-chakra-crown/40 text-xs">({k.english})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-heading ${sc.text}`}>{k.score?.toFixed(1)}/5</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${sc.bg} ${sc.text}`}>{k.status}</span>
                  {k.recommendations?.length > 0 && (
                    isExpanded
                      ? <ChevronUp className="w-3 h-3 text-chakra-crown/30" />
                      : <ChevronDown className="w-3 h-3 text-chakra-crown/30" />
                  )}
                </div>
              </div>
              <div className="h-1.5 bg-cosmic-deep rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${sc.bar}`} style={{ width: `${k.percentage}%` }} />
              </div>
              {isExpanded && k.recommendations?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-divine-gold/10 space-y-1.5">
                  <span className="text-[10px] text-chakra-crown/40 uppercase tracking-wide">Recommendations</span>
                  {k.recommendations.map((rec, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <ArrowRight className="w-3 h-3 text-divine-gold mt-0.5 shrink-0" />
                      <p className="text-[11px] text-chakra-crown/60 leading-relaxed">{rec}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Priority Koshas */}
      {scores.priority_koshas?.length > 0 && (
        <div className="p-2.5 rounded-lg border border-amber-500/20 bg-amber-500/5">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-amber-400 font-heading text-xs">PRIORITY FOCUS:</span>
            <span className="text-chakra-crown/60 text-xs">
              {scores.priority_koshas.map(kid => scores.koshas?.[kid]?.name || kid).join(", ")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
